import React, { useEffect, useState } from "react";
import { Wifi, ScanLine } from "lucide-react";
import { getRFIDReader, resolveReaderMode } from "@/lib/rfid/IRFIDReader";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

/**
 * FAB / Wide button to trigger a chip scan.
 * - Editorial Rural-Tech: solid eucalyptus, monospace meta-label, scan-pulse on read.
 */
export function ScanCTA({ onChip, label = "Escanear chip", autoConnect = true, fullWidth = true, testId = "scan-chip-button" }) {
  const [status, setStatus] = useState("disconnected");
  const [mode, setMode] = useState("simulator");
  const [pulsing, setPulsing] = useState(false);

  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;
    (async () => {
      const m = await resolveReaderMode();
      if (cancelled) return;
      setMode(m);
      const reader = await getRFIDReader();
      if (autoConnect && reader.connect) {
        try { await reader.connect(); } catch (_) {}
      }
      unsubscribe = reader.onChipRead((chip) => {
        setPulsing(true);
        setTimeout(() => setPulsing(false), 800);
        onChip && onChip(chip);
      });
      setStatus(reader.getStatus ? reader.getStatus() : "connected");
    })();
    return () => { cancelled = true; unsubscribe(); };
  }, [autoConnect, onChip]);

  const trigger = async () => {
    const reader = await getRFIDReader();
    try {
      if (mode === "simulator") {
        const c = reader.emitRandomChip?.();
        if (!c) toast.warning("Simulador sin chips registrados");
      } else if (mode === "hid") {
        await reader.connect();
        toast.info("Listo: usá el gatillo del lector para escanear.");
      } else {
        await reader.connect();
      }
      setStatus(reader.getStatus?.() || "connected");
    } catch (e) {
      toast.error(`No pude conectar el lector: ${e.message || e}`);
    }
  };

  const Icon = mode === "hid" ? Wifi : ScanLine;
  const modeLabel = mode === "simulator" ? "sim" : mode;

  return (
    <button
      onClick={trigger}
      data-testid={testId}
      className={cn(
        "group inline-flex items-center justify-center gap-2.5 h-14 px-6 rounded-[var(--radius)] bg-primary text-primary-foreground text-base font-medium",
        "shadow-paper hover:opacity-95 active:scale-[0.98] transition-[transform,opacity,box-shadow] duration-150",
        "ring-focus",
        pulsing && "scan-pulse",
        fullWidth && "w-full"
      )}
    >
      <Icon size={20} className="shrink-0" />
      <span className="font-heading tracking-tight">{label}</span>
      <span className="ml-1 text-[10.5px] font-mono opacity-70 uppercase tracking-[0.08em]">{modeLabel}</span>
    </button>
  );
}
